import { cloneDeep, pick } from 'lodash';
import uuid from 'uuid/v4';
import api from '../api';

const initialState = {
  master: {
    rows: [],
    moduleEvents: [],
  },
  draft: {
    rows: [],
    moduleEvents: [],
  },
  config: {
    availableModules: [],
    modules: [],
    slots: [],
  },
  dirty: false,
};

const getters = {
  rows: state => state.draft.rows,
  moduleEventById: state => (eventId) => {
    const draftModule = state.draft.moduleEvents.find(event => event.uuid === eventId);
    const masterModule = state.master.moduleEvents.find(event => event.uuid === eventId);

    if (!draftModule) {
      return masterModule;
    }

    return {
      ...masterModule,
      ...draftModule,
    };
  },
  availableModules: state => state.config.modules.filter(module => state.config.availableModules.includes(module.type)),
};

const actions = {
  async INIT_EDITOR({ dispatch, commit }, blockId) {
    dispatch('RESET_EDITOR');
    const daypartingBlock = await api.fetch(blockId);
    const config = await api.fetchConfig(blockId);
    commit('SET_MASTER', daypartingBlock);
    commit('SET_CONFIG', config);
  },
  ADD_ROW({ commit }) {
    commit('ADD_ROW', uuid());
  },
  RESET_EDITOR({ commit }) {
    commit('RESET_EDITOR');
  },
  RESET_MODULE_EVENT_EDITOR({ commit }) {
    commit('RESET_MODULE_EVENT_EDITOR');
  },
  ADD_MODULE_EVENT({ commit }, { row, moduleEvent }) {
    commit('ADD_MODULE_EVENT', { rowId: row, moduleEvent });
  },
  async SAVE({ state, commit }, blockId) {
    const daypartingBlock = await api.update(blockId, state.draft);
    commit('SET_MASTER', daypartingBlock);
  },
  async REMOVE_MODULE_EVENT({ state, commit }, moduleEvent) {
    commit('REMOVE_MODULE_EVENT', moduleEvent);
  },
  async EDIT_MODULE_EVENT({ state, commit, getters }, { id, event }) {
    const moduleEvent = cloneDeep(getters.moduleEventById(id));
    moduleEvent.event = cloneDeep(event);
    commit('EDIT_MODULE_EVENT', { id, moduleEvent });
  },
};

const mutations = {
  RESET_EDITOR(state) {
    state = cloneDeep(initialState);
  },
  SET_MASTER(state, block) {
    state.master = block;
    state.master.moduleEvents = block.rows.reduce((result, row) => [...result, ...row.moduleEvents], []);
    state.draft.rows = cloneDeep(block.rows);
    state.draft.rows.forEach((row) => {
      row.moduleEvents = row.moduleEvents.map(event => event.uuid);
    });
    state.draft.moduleEvents = [];
    state.dirty = false;
  },
  SET_CONFIG(state, config) {
    state.config = config;
  },
  RESET_DRAFT(state) {
    state.draft = cloneDeep(state.master.rows);
    state.draft.moduleEvents = [];
    state.dirty = false;
  },
  ADD_ROW(state, rowId) {
    state.draft.rows.push(
      {
        uuid: rowId,
        moduleEvents: [],
      },
    );
    state.dirty = true;
  },
  ADD_MODULE_EVENT(state, { rowId, moduleEvent }) {
    const row = state.draft.rows.find(item => item.uuid === rowId);
    if (!row) {
      console.error(`Wrong row ${rowId}`);
    }
    row.moduleEvents.push(moduleEvent.uuid);
    state.draft.moduleEvents.push(cloneDeep(moduleEvent));
    state.dirty = true;
  },
  REMOVE_MODULE_EVENT(state, id) {
    state.draft.moduleEvents = state.draft.moduleEvents.filter(event => event.uuid !== id);
    state.draft.rows.forEach((row) => {
      row.moduleEvents = row.moduleEvents.filter(eventId => eventId !== id);
    });
    state.dirty = true;
  },
  EDIT_MODULE_EVENT(state, { id, moduleEvent }) {
    const module = state.draft.moduleEvents.find(event => event.uuid === id);
    if (!module) {
      state.draft.moduleEvents.push({
        id: moduleEvent.id,
        uuid: moduleEvent.uuid,
        event: moduleEvent.event,
      });
    } else {
      module.event = moduleEvent.event;
    }
    state.dirty = true;
  },
};

export default {
  namespaced: true,
  state: cloneDeep(initialState),
  actions,
  getters,
  mutations,
};
